import {
    autoUpdate,
    flip,
    offset,
    shift,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react';
import type { LoadedFrom } from '@nowline-app/share';
import { useId, useRef, useState } from 'react';
import { EXAMPLES } from '../examples/index.ts';
import { SecurityBadge } from '../SecurityBadge.tsx';
import { copyEmbedSnippet, copyShareLink } from '../share/share.ts';
import type { Theme } from '../theme/index.ts';
import './AppHeader.css';

export type ViewMode = 'editor' | 'both' | 'diagram';

interface AppHeaderProps {
    source: string;
    loadedFrom: LoadedFrom;
    viewMode: ViewMode;
    onViewModeChange: (mode: ViewMode) => void;
    theme: Theme;
    onThemeChange: (t: Theme) => void;
    isFullscreen: boolean;
    onFullscreenToggle: () => void;
    onLoadExample: (source: string) => void;
    /** Whether the current source differs from the initial load / any example. */
    isDirty: boolean;
    /** When true, the Both option is disabled — viewport is too narrow for side-by-side. */
    bothDisabled?: boolean;
}

// ── Segmented control ────────────────────────────────────────────────────────

const VIEW_MODES: { value: ViewMode; label: string }[] = [
    { value: 'editor', label: 'Editor' },
    { value: 'both', label: 'Both' },
    { value: 'diagram', label: 'Diagram' },
];

function SegmentedControl({
    value,
    onChange,
    bothDisabled,
}: {
    value: ViewMode;
    onChange: (v: ViewMode) => void;
    bothDisabled?: boolean;
}) {
    return (
        <fieldset className='seg-ctrl' aria-label='View mode'>
            {VIEW_MODES.map((mode) => {
                const isDisabled = mode.value === 'both' && bothDisabled;
                return (
                    <button
                        key={mode.value}
                        type='button'
                        aria-pressed={value === mode.value}
                        disabled={isDisabled}
                        className={[
                            'seg-btn',
                            value === mode.value ? 'seg-btn--active' : '',
                            isDisabled ? 'seg-btn--disabled' : '',
                        ]
                            .filter(Boolean)
                            .join(' ')}
                        onClick={() => onChange(mode.value)}
                    >
                        {mode.label}
                    </button>
                );
            })}
        </fieldset>
    );
}

// ── Theme toggle ─────────────────────────────────────────────────────────────

const THEME_OPTIONS: { value: Theme; label: string }[] = [
    { value: 'auto', label: 'Auto' },
    { value: 'light', label: 'Light' },
    { value: 'dark', label: 'Dark' },
];

function ThemeIcon({ theme }: { theme: Theme }) {
    if (theme === 'light') {
        return (
            <svg
                viewBox='0 0 16 16'
                width='15'
                height='15'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.4'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
            >
                <circle cx='8' cy='8' r='3' />
                <path d='M8 1v1.5M8 13.5V15M2.7 2.7l1.1 1.1M12.2 12.2l1.1 1.1M1 8h1.5M13.5 8H15M2.7 13.3l1.1-1.1M12.2 3.8l1.1-1.1' />
            </svg>
        );
    }
    if (theme === 'dark') {
        return (
            <svg
                viewBox='0 0 16 16'
                width='15'
                height='15'
                fill='none'
                stroke='currentColor'
                strokeWidth='1.4'
                strokeLinecap='round'
                strokeLinejoin='round'
                aria-hidden='true'
            >
                <path d='M13.5 9.7A5.6 5.6 0 0 1 6.3 2.5 5.75 5.75 0 1 0 13.5 9.7z' />
            </svg>
        );
    }
    // Auto — half-filled circle
    return (
        <svg
            viewBox='0 0 16 16'
            width='15'
            height='15'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.4'
            strokeLinecap='round'
            strokeLinejoin='round'
            aria-hidden='true'
        >
            <circle cx='8' cy='8' r='6' />
            <path d='M8 2a6 6 0 0 0 0 12z' fill='currentColor' stroke='none' />
        </svg>
    );
}

function ThemeToggle({
    theme,
    onThemeChange,
}: {
    theme: Theme;
    onThemeChange: (t: Theme) => void;
}) {
    const [open, setOpen] = useState(false);
    const menuId = useId();

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: 'bottom-end',
        middleware: [offset(4), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    return (
        <>
            <button
                ref={refs.setReference}
                type='button'
                aria-label={`Theme: ${theme}`}
                aria-haspopup='menu'
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                className='hdr-icon-btn'
                title={`Theme: ${theme}`}
                {...getReferenceProps()}
            >
                <ThemeIcon theme={theme} />
            </button>
            {open && (
                <div
                    ref={refs.setFloating}
                    id={menuId}
                    role='menu'
                    className='menu-list'
                    style={floatingStyles}
                    {...getFloatingProps()}
                >
                    {THEME_OPTIONS.map((opt) => (
                        <div key={opt.value} role='none'>
                            <button
                                type='button'
                                role='menuitemradio'
                                aria-checked={theme === opt.value}
                                className={`menu-item${theme === opt.value ? ' menu-item--checked' : ''}`}
                                onClick={() => {
                                    onThemeChange(opt.value);
                                    setOpen(false);
                                }}
                            >
                                {opt.label}
                                {theme === opt.value && (
                                    <span className='menu-item-check' aria-hidden='true'>
                                        ✓
                                    </span>
                                )}
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

// ── Examples menu ─────────────────────────────────────────────────────────────

function ExamplesMenu({
    onLoadExample,
    isDirty,
}: {
    onLoadExample: (source: string) => void;
    isDirty: boolean;
}) {
    const [open, setOpen] = useState(false);
    const menuId = useId();

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: 'bottom-start',
        middleware: [offset(4), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    function handleLoad(source: string) {
        if (isDirty && !window.confirm('Replace your current diagram with this example?')) return;
        onLoadExample(source);
        setOpen(false);
    }

    return (
        <>
            <button
                ref={refs.setReference}
                type='button'
                aria-haspopup='menu'
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                className='hdr-text-btn'
                {...getReferenceProps()}
            >
                Examples
                <span aria-hidden='true' className='hdr-chevron'>
                    {open ? '▲' : '▼'}
                </span>
            </button>
            {open && (
                <div
                    ref={refs.setFloating}
                    id={menuId}
                    role='menu'
                    className='menu-list examples-menu'
                    style={floatingStyles}
                    {...getFloatingProps()}
                >
                    {EXAMPLES.map((ex) => (
                        <div key={ex.id} role='none'>
                            <button
                                type='button'
                                role='menuitem'
                                className='menu-item examples-item'
                                onClick={() => handleLoad(ex.source)}
                            >
                                <span className='examples-item-label'>{ex.label}</span>
                                <span className='examples-item-desc'>{ex.description}</span>
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
}

// ── Share menu ────────────────────────────────────────────────────────────────

function ShareMenu({ source, loadedFrom }: { source: string; loadedFrom: LoadedFrom }) {
    const [open, setOpen] = useState(false);
    const [toast, setToast] = useState<string | null>(null);
    const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const menuId = useId();

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: 'bottom-start',
        middleware: [offset(4), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    function showToast(msg: string) {
        if (toastTimerRef.current !== null) clearTimeout(toastTimerRef.current);
        setToast(msg);
        toastTimerRef.current = setTimeout(() => setToast(null), 3000);
    }

    return (
        <div className='share-menu-root'>
            <button
                ref={refs.setReference}
                type='button'
                aria-haspopup='menu'
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                className='hdr-text-btn'
                {...getReferenceProps()}
            >
                Share
                <span aria-hidden='true' className='hdr-chevron'>
                    {open ? '▲' : '▼'}
                </span>
            </button>
            {toast !== null && (
                <div role='status' aria-live='polite' className='hdr-toast'>
                    {toast}
                </div>
            )}
            {open && (
                <div
                    ref={refs.setFloating}
                    id={menuId}
                    role='menu'
                    className='menu-list'
                    style={floatingStyles}
                    {...getFloatingProps()}
                >
                    <div role='none'>
                        <button
                            type='button'
                            role='menuitem'
                            className='menu-item'
                            onClick={() => {
                                void copyShareLink(source, loadedFrom)
                                    .then(() => showToast('Link copied'))
                                    .catch(() => showToast('Copy failed'));
                                setOpen(false);
                            }}
                        >
                            Copy share link
                        </button>
                    </div>
                    <div role='none'>
                        <button
                            type='button'
                            role='menuitem'
                            className='menu-item'
                            onClick={() => {
                                void copyEmbedSnippet(source)
                                    .then(() => showToast('Embed snippet copied'))
                                    .catch(() => showToast('Copy failed'));
                                setOpen(false);
                            }}
                        >
                            <span aria-hidden='true' className='menu-item-icon'>
                                {'{ }'}
                            </span>
                            Copy embed snippet
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Narrow-screen view-mode menu (≤600px) ─────────────────────────────────────
// The segmented control is hidden on narrow viewports; this compact dropdown
// replaces it so the user can still switch between Editor / Both / Diagram.

function ViewModeMenu({
    value,
    onChange,
    bothDisabled,
}: {
    value: ViewMode;
    onChange: (v: ViewMode) => void;
    bothDisabled?: boolean;
}) {
    const [open, setOpen] = useState(false);
    const menuId = useId();
    const currentLabel = VIEW_MODES.find((m) => m.value === value)?.label ?? value;

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: 'bottom-end',
        middleware: [offset(4), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'menu' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    return (
        <>
            <button
                ref={refs.setReference}
                type='button'
                aria-label={`View mode: ${currentLabel}`}
                aria-haspopup='menu'
                aria-expanded={open}
                aria-controls={open ? menuId : undefined}
                className='hdr-text-btn app-header__narrow-vm'
                {...getReferenceProps()}
            >
                {currentLabel}
                <span aria-hidden='true' className='hdr-chevron'>
                    {open ? '▲' : '▼'}
                </span>
            </button>
            {open && (
                <div
                    ref={refs.setFloating}
                    id={menuId}
                    role='menu'
                    className='menu-list'
                    style={floatingStyles}
                    {...getFloatingProps()}
                >
                    {VIEW_MODES.map((mode) => {
                        const isDisabled = mode.value === 'both' && bothDisabled;
                        return (
                            <div key={mode.value} role='none'>
                                <button
                                    type='button'
                                    role='menuitemradio'
                                    aria-checked={value === mode.value}
                                    aria-disabled={isDisabled}
                                    className={`menu-item${value === mode.value ? ' menu-item--checked' : ''}${isDisabled ? ' menu-item--disabled' : ''}`}
                                    onClick={() => {
                                        if (isDisabled) return;
                                        onChange(mode.value);
                                        setOpen(false);
                                    }}
                                >
                                    {mode.label}
                                    {value === mode.value && (
                                        <span className='menu-item-check' aria-hidden='true'>
                                            ✓
                                        </span>
                                    )}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </>
    );
}

function UpgradePopover() {
    const [open, setOpen] = useState(false);
    const popoverId = useId();

    const { refs, floatingStyles, context } = useFloating({
        open,
        onOpenChange: setOpen,
        placement: 'bottom-end',
        middleware: [offset(8), flip(), shift({ padding: 8 })],
        whileElementsMounted: autoUpdate,
    });

    const click = useClick(context);
    const dismiss = useDismiss(context);
    const role = useRole(context, { role: 'dialog' });
    const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

    return (
        <>
            <button
                ref={refs.setReference}
                type='button'
                className='hdr-upgrade-btn'
                aria-haspopup='dialog'
                aria-expanded={open}
                {...getReferenceProps()}
            >
                Upgrade to Pro
            </button>
            {open && (
                <div
                    ref={refs.setFloating}
                    id={popoverId}
                    role='dialog'
                    aria-modal='false'
                    aria-labelledby={`${popoverId}-title`}
                    className='upgrade-popover'
                    style={floatingStyles}
                    {...getFloatingProps()}
                >
                    <button
                        type='button'
                        className='upgrade-popover-close'
                        aria-label='Close'
                        onClick={() => setOpen(false)}
                    >
                        ×
                    </button>
                    <p id={`${popoverId}-title`} className='upgrade-popover-title'>
                        Nowline Pro — coming soon
                    </p>
                    <p className='upgrade-popover-body'>
                        Real-time collaboration, GitHub sync, and a visual canvas are in
                        development. Join the waitlist to be notified when Pro launches.
                    </p>
                    <a
                        href='mailto:hello@nowline.io?subject=Nowline%20Pro%20waitlist'
                        className='upgrade-popover-cta'
                        onClick={() => setOpen(false)}
                    >
                        Join the waitlist →
                    </a>
                </div>
            )}
        </>
    );
}

// ── Fullscreen button ─────────────────────────────────────────────────────────

function FullscreenButton({
    isFullscreen,
    onToggle,
}: {
    isFullscreen: boolean;
    onToggle: () => void;
}) {
    return (
        <button
            type='button'
            className='hdr-icon-btn'
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
            onClick={onToggle}
        >
            {isFullscreen ? <ExitFullscreenIcon /> : <FullscreenIcon />}
        </button>
    );
}

function FullscreenIcon() {
    return (
        <svg
            viewBox='0 0 16 16'
            width='15'
            height='15'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            aria-hidden='true'
        >
            <path d='M1 1h4M1 1v4M15 1h-4M15 1v4M1 15h4M1 15v-4M15 15h-4M15 15v-4' />
        </svg>
    );
}

function ExitFullscreenIcon() {
    return (
        <svg
            viewBox='0 0 16 16'
            width='15'
            height='15'
            fill='none'
            stroke='currentColor'
            strokeWidth='1.5'
            strokeLinecap='round'
            aria-hidden='true'
        >
            <path d='M5 1v4H1M15 5h-4V1M1 11h4v4M11 15v-4h4' />
        </svg>
    );
}

// ── Logo ──────────────────────────────────────────────────────────────────────

function NowlineLogo() {
    return (
        <a href='/' className='hdr-logo' aria-label='Nowline home'>
            {/* CSS shows/hides based on [data-theme], mirroring site Header.astro */}
            <img
                src='/logo-light.svg'
                alt='Nowline'
                className='hdr-logo-img hdr-logo-light'
                width='90'
                height='41'
            />
            <img
                src='/logo-dark.svg'
                alt=''
                className='hdr-logo-img hdr-logo-dark'
                aria-hidden='true'
                width='90'
                height='41'
            />
        </a>
    );
}

// ── Main AppHeader ────────────────────────────────────────────────────────────

export function AppHeader({
    source,
    loadedFrom,
    viewMode,
    onViewModeChange,
    theme,
    onThemeChange,
    isFullscreen,
    onFullscreenToggle,
    onLoadExample,
    isDirty,
    bothDisabled,
}: AppHeaderProps) {
    return (
        <header className='app-header'>
            <div className='app-header__left'>
                <NowlineLogo />
            </div>

            <div className='app-header__centre'>
                <SegmentedControl
                    value={viewMode}
                    onChange={onViewModeChange}
                    {...(bothDisabled !== undefined ? { bothDisabled } : {})}
                />
            </div>

            <div className='app-header__right'>
                <ExamplesMenu onLoadExample={onLoadExample} isDirty={isDirty} />
                <ShareMenu source={source} loadedFrom={loadedFrom} />
                <SecurityBadge />
                <ThemeToggle theme={theme} onThemeChange={onThemeChange} />
                <ViewModeMenu
                    value={viewMode}
                    onChange={onViewModeChange}
                    {...(bothDisabled !== undefined ? { bothDisabled } : {})}
                />
                <UpgradePopover />
                <FullscreenButton isFullscreen={isFullscreen} onToggle={onFullscreenToggle} />

                {/* Reserved slot — Pro fills with collaborator avatar stack */}
                <div className='app-header__collab-region' aria-hidden='true' />
            </div>
        </header>
    );
}
